<template>
    <div>
        <ClientOnly>
            <VJoyDialog
                id="privacy-policy-update-dialog"
                ref="gdprDialog"
                pattern-confirm
                :confirm-text="$t('general.ok')"
                :focus-trap="false"
                @dialog:confirm="acceptGdpr"
            >
                <template #dialog-header>
                    <MaltIllustration width="180" class="banner-img" name="folders-lock--high" alt="" />
                    <p>{{ $t('gdpr.acceptance.popup.title') }}</p>
                </template>
                <template #dialog-body>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-html="$t('gdpr.acceptance.popup.message', [''])" />
                </template>
            </VJoyDialog>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
    import {nextTick, ref, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltIllustration} from '#components';
    import {onMounted, useAuth, useContent, useLocale, useWebPreferences} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyDialog} from '@maltjoy/core-vue';

    const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
    const GDPR_UPDATE_ACKNOWLEDGEMENT_ID = 'gdpr-version-acceptance-acknowledgement';
    const LAST_GDPR_VERSION_HUMAND_ID = 'policy';
    const LAST_GDPR_VERSION_TYPE = 'privacy';
    const {locale} = useLocale();

    const shouldDisplayModal = ref(false);
    const isGdprModalOnUpdateEnabled = useFeatureFlag('freelancer-gdpr-modal-on-update');
    const gdprDialog = useTemplateRef('gdprDialog');
    const auth = useAuth();
    const {loggedIn} = storeToRefs(auth);

    const props = withDefaults(
        defineProps<{
            // Should only be used for tests
            forceLoggedIn?: boolean;
        }>(),
        {
            forceLoggedIn: false,
        },
    );

    onMounted(() => {
        checkIfGdprShouldBeDisplayed();
    });

    async function checkIfGdprShouldBeDisplayed() {
        if (loggedIn.value || props.forceLoggedIn) {
            if (isGdprModalOnUpdateEnabled.value) {
                const gdprVersion = await getGdprVersionAcceptedByCurrentUser();
                const lastGdprVersion = await getLastAvailableGdprVersion();

                shouldDisplayModal.value = shouldAskForAcceptanceOfLatestTc(gdprVersion || '', lastGdprVersion || '');

                if (shouldDisplayModal.value) {
                    await nextTick();
                    gdprDialog.value?.show();
                }
            }
        }
    }

    function acceptGdpr() {
        useWebPreferences().setWebPref(GDPR_UPDATE_ACKNOWLEDGEMENT_ID, new Date().toISOString().substring(0, 10));
        gdprDialog.value?.hide();
    }

    function getGdprVersionAcceptedByCurrentUser() {
        return useWebPreferences().getWebPref(GDPR_UPDATE_ACKNOWLEDGEMENT_ID);
    }

    function getLastAvailableGdprVersion() {
        return useContent().getLastVersionForContent(LAST_GDPR_VERSION_HUMAND_ID, locale.value!, LAST_GDPR_VERSION_TYPE);
    }

    function shouldAskForAcceptanceOfLatestTc(tcVersion: string, lastTcVersion: string) {
        if (lastTcVersion === '') {
            lastTcVersion = new Date().toISOString().substring(0, 10);
        }

        if (tcVersion === '') {
            return true;
        }

        return isFirstIsoTextDateBeforeSecondIsoTextDate(tcVersion, lastTcVersion);
    }

    function isFirstIsoTextDateBeforeSecondIsoTextDate(dateAsStringCheckedForBeingBefore: string, referenceDateAsString: string) {
        const dateCheckedForBeingBefore = toDateIfValidIsoDate(dateAsStringCheckedForBeingBefore);
        const referenceDate = toDateIfValidIsoDate(referenceDateAsString);
        return dateCheckedForBeingBefore < referenceDate;
    }

    function toDateIfValidIsoDate(dateAsString: string) {
        if (!ISO_DATE_RE.test(dateAsString)) {
            throw new Error(`Invalid ISO date: ${dateAsString}`);
        }

        return new Date(dateAsString);
    }
</script>

<style lang="scss" scoped>
    .banner-img {
        display: block;
        margin: 0 auto var(--joy-core-spacing-4);
    }
</style>
